/**
 * LES OUTILS DE RELIEF, partages par les deux iles.
 *
 * Rien ici n'est nouveau: ce sont les aides que `shared/sim/terrain.ts` s'etait
 * ecrites pour l'ile 1, sorties telles quelles — au chiffre pres — le jour ou
 * l'ile 2 en a eu besoin a son tour. Les recopier aurait ete « deux regles pour
 * la meme question », le piege que ce projet paie le plus cher: le jour ou l'on
 * corrige un coin arrondi d'un cote, l'autre garde son angle droit et personne
 * ne s'en apercoit avant qu'un joueur reste coince dedans.
 *
 * La preuve que l'extraction est fidele n'est pas une relecture: c'est
 * `tests/unit/monde2.test.ts`, qui exige que l'ile 1 rende EXACTEMENT le meme
 * terrain qu'avant, sommet par sommet.
 */

import { clamp, lerp } from './alea.ts'

// ------------------------------------------------------- champs a une variable

/**
 * Plusieurs champs du monde ne dependent que d'UNE coordonnee: la deformation
 * des frontieres de biome le long de la frontiere, la position du trait de cote
 * le long de chaque bord, la sinuosite d'une riviere le long de son cours. Les
 * echantillonner a chaque sommet coutait 80 hachages par point, soit la moitie
 * du temps de construction du monde, pour redecouvrir 501 valeurs.
 *
 * Ils sont donc tabules au metre et lus par interpolation lineaire. Les motifs
 * les plus fins font 30 m: l'erreur d'interpolation est de quelques
 * centimetres, invisible, et la construction du terrain passe de 200 a 90 ms.
 *
 * La table porte sa propre longueur (`t.length - 1` metres): c'est la seule
 * chose qui a change en sortant d'ici, et c'est ce qui permet a une ile de 700 m
 * d'utiliser le meme outil qu'une ile de 500.
 */
export function lireTable(t: Float64Array, u: number): number {
  const dernier = t.length - 1
  if (u <= 0) return t[0] ?? 0
  if (u >= dernier) return t[dernier] ?? 0
  const i = Math.floor(u)
  const a = t[i] ?? 0
  const b = t[i + 1] ?? a
  return a + (b - a) * (u - i)
}

/** Tabule un champ a une variable, au metre, sur `cote + 1` valeurs. */
export function construireTable(cote: number, graine: number, f: (u: number, graine: number) => number): Float64Array {
  const t = new Float64Array(cote + 1)
  for (let i = 0; i <= cote; i++) t[i] = f(i, graine)
  return t
}

// ------------------------------------------------------------------ littoral

/**
 * Minimum adouci. Sur les derniers metres avant le croisement de deux traits
 * de cote, les deux distances se fondent au lieu de se couper: les quatre coins
 * d'une ile deviennent des caps arrondis et non des angles droits.
 */
export function minAdouci(a: number, b: number, k: number): number {
  const h = clamp(0.5 + (0.5 * (b - a)) / k, 0, 1)
  return lerp(b, a, h) - k * h * (1 - h)
}
